import React, { FC } from 'react';
import './costCode.scss';
import WarningBox from 'components/Elements/WarningBox';
import TextInput from 'components/Elements/TextInput';
import Tooltip from 'components/Elements/Tootltip';
import { WarningIcon } from '../../Elements/CustomSVGIcon';
import FormSection from '../FormSection';

interface CostCodeProps {
    onChangeCode: (code: string) => void;
    onChangeEmail?: (email: string) => void;
    code: string;
    email?: string;
    // Optional — defaults to false. When true, the email field is shown and
    // an onChangeEmail handler must be supplied (guarded at runtime below).
    requireEmail?: boolean;
    // The overlap/chargeback explainer is only relevant to visit requests
    // (where another requester can already hold the same day). Other
    // request types opt out by passing showInfo={false}.
    showInfo?: boolean;
    // Billable hours for the visit. When explicitly 0 (open day / 24-7 site)
    // there is nothing to charge, so the cost code is marked not applicable.
    // Left undefined by request types that don't compute billable hours.
    billedHours?: number;
}

const CostCode: FC<CostCodeProps> = ({ onChangeCode, onChangeEmail, code, email = '', requireEmail = false, showInfo = true, billedHours }) => {
    // Guard against a required email field with no handler — typing would
    // silently do nothing. A dynamic boolean for requireEmail rules out a
    // compile-time discriminated union, so assert at runtime instead.
    if (requireEmail && !onChangeEmail) {
        // eslint-disable-next-line no-console
        console.error('CostCode: requireEmail is true but onChangeEmail was not provided — email input changes will be ignored.');
    }

    // When the visit has no billable hours (e.g. an open day or a 24/7 site)
    // there is nothing to charge, so the cost code is not applicable. Only
    // applies when a caller actually supplies billedHours.
    const isNotApplicable = typeof billedHours === 'number' && billedHours <= 0;

    const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChangeCode(e.target.value);
    };

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChangeEmail?.(e.target.value);
    };

    const getTooltip = () => (
        <div>
            <p>You only need to enter one cost code here. This can be in the format of the following:
                <br />
                <span className="tooltip-bold"><b>Project Code:</b></span> N00001
                <br />
                <span className="tooltip-bold"><b>Cost Centre:</b></span>   GB00001
                <br />
                <span className="tooltip-bold"><b>Vodafone PO Code:</b></span>
                1500001
            </p>
        </div>
    );

    return (
        <FormSection title="Enter Cost Code">
            <div className="cost-code-container">
                <div className="code-container">
                    {showInfo && (
                        <WarningBox>If you have requested to book a visit on a day another person has already submitted a request for, you still need to enter a Cost Code, but you will not be charged.
                            <br /><br /> Please be aware, if the current approved requester cancels for any reason, you will then be charged against the cost code you provide.
                        </WarningBox>
                    )}
                    <div className="heading-wrapper">
                        <h5 className="heading">Cost Centre, Project Code or PO</h5>
                        <Tooltip title={getTooltip()}>
                            <div>
                                <WarningIcon />
                            </div>
                        </Tooltip>
                    </div>
                    <div className="input-wrapper">
                        {isNotApplicable ? (
                            <TextInput value="N/A" disabled onChange={() => undefined} />
                        ) : (
                            <TextInput onChange={handleCodeChange} value={code} />
                        )}
                    </div>
                </div>

                {requireEmail && (
                    <div className="contact-container">
                        <WarningBox>To continue with your request, please enter the email address of the Vodafone Sponsor/Manager who provided you with the Cost Centre, Project Code or PO.
                            <br /><br /> Request for a Non-Open Day is chargeable, however any request for an Open-Day will not require Cost Centre, Project Code or PO.
                        </WarningBox>
                        <div className="heading-wrapper">
                            <h5 className="heading" style={{ marginTop: '20px' }}>Vodafone Email Address for Cost Approval</h5>
                        </div>
                        <div className="input-wrapper">
                            <TextInput onChange={handleEmailChange} value={email} />
                        </div>
                    </div>
                )}
            </div>
        </FormSection>
    );
};

export default CostCode;
